import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ReportDateRangePicker } from './date-range/ReportDateRangePicker';
import {
  businessTodayAsLocalDate,
  makePreset,
  type ReportPeriod
} from './date-range/reportPeriod';
import { FundedSummaryCards } from './components/FundedSummaryCards';
import { FundedDataTable } from './components/FundedDataTable';
import { MonthlyProductionReport } from './components/MonthlyProductionReport';
import { FundedSummaryReport } from './components/FundedSummaryReport';
import {
  getLoanTypeCategory,
  getNonQmSubtype,
  getPurposeCategory,
  getChannelCategory,
  getRateTypeCategory,
  getStateCategory,
  getStateLabels,
  buildBreakdown,
  buildNonQmSubBreakdown,
  LOAN_TYPE_LABELS,
  PURPOSE_LABELS,
  CHANNEL_LABELS,
  RATE_TYPE_LABELS,
  type BreakdownRow
} from './components/fundedSummaryModel';
import {
  PipelineLoan,
  getBorrowerName,
  getLoanOfficerName
} from './types';
import { getStrictFundedDate, groupLoansByMonth } from './utils';
import { fetchCanonicalReportData } from './reporting';
import { AlertTriangle, DollarSign, RefreshCw, Download, Table2, BarChart3, CalendarRange } from 'lucide-react';
import * as ExcelJS from 'exceljs';
import { createComponentLogger } from '@/utils/logger';
import { parseISO, format, differenceInDays } from 'date-fns';

const log = createComponentLogger('FundedLoanReport', 'reports');

interface FundedReportFilters {
  loanOfficer: string;
  investor: string;
  channel: string;
  productType: string;
}

export function FundedLoanReport() {
  const [loans, setLoans] = useState<PipelineLoan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dataWarnings, setDataWarnings] = useState<string[]>([]);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
  const [activeTab, setActiveTab] = useState<'summary' | 'details' | 'monthly-production'>('summary');
  const [period, setPeriod] = useState<ReportPeriod>(() => (
    makePreset('ytd', businessTodayAsLocalDate())
  ));
  const [filters, setFilters] = useState<FundedReportFilters>({
    loanOfficer: 'all',
    investor: 'all',
    channel: 'all',
    productType: 'all'
  });

  // Fetch all loans that could be funded
  const fetchLoans = async () => {
    const today = businessTodayAsLocalDate();
    setIsLoading(true);
    setLoadError(null);
    setDataWarnings([]);
    setPeriod((current) => (
      current.mode === 'mtd' || current.mode === 'qtd' || current.mode === 'ytd'
        ? makePreset(current.mode, today)
        : current
    ));
    try {
      const result = await fetchCanonicalReportData();
      if (result.loanError || result.payoutError) {
        const message = result.loanError?.message || result.payoutError?.message;
        setLoadError(`${message || 'Unable to load funded production.'} Refresh to try again.`);
        setLoans([]);
        return;
      }
      const fundedLoans = result.loans.filter(
        (loan) => getStrictFundedDate(loan) !== null
      );

      setLoans(fundedLoans);
      setRefreshedAt(new Date());
      if (result.payoutWarnings.length > 0) {
        setDataWarnings([
          `${result.payoutWarnings.length} payout match conflict${result.payoutWarnings.length === 1 ? '' : 's'} resolved to the earliest fund date.`
        ]);
      }
      log.info('Fetched funded loans', { metadata: { count: fundedLoans.length } });
    } catch (error) {
      setLoadError('Unable to load funded production. Refresh to try again.');
      log.error('Error fetching funded loans', {}, error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLoans();
  }, []);

  // Get unique values for filter dropdowns (computed from funded loans)
  const loanOfficers = useMemo(() => {
    const officers = new Set<string>();
    loans.forEach((loan) => {
      const loName = getLoanOfficerName(loan);
      if (loName && loName !== '-') {
officers.add(loName);
}
    });
    return Array.from(officers).sort();
  }, [loans]);

  const investors = useMemo(() => {
    const invs = new Set<string>();
    loans.forEach((loan) => {
      if (loan.investor_name) {
invs.add(loan.investor_name);
}
    });
    return Array.from(invs).sort();
  }, [loans]);

  const channels = useMemo(() => {
    const chans = new Set<string>();
    loans.forEach((loan) => {
      if (loan.channel) {
chans.add(loan.channel);
}
    });
    return Array.from(chans).sort();
  }, [loans]);

  const productTypes = useMemo(() => {
    const products = new Set<string>();
    loans.forEach((loan) => {
      if (loan.rate_lock_loan_program) {
products.add(loan.rate_lock_loan_program);
}
    });
    return Array.from(products).sort();
  }, [loans]);

  // Apply all filters
  const filteredLoans = useMemo(() => {
    return loans.filter((loan) => {
      // Date range filter — on strict funded date
      const { from, to } = period;
      const fundedDate = getStrictFundedDate(loan);
      if (from && fundedDate) {
        const fromKey = format(from, 'yyyy-MM-dd');
        const toKey = to ? format(to, 'yyyy-MM-dd') : fromKey;
        if (fundedDate < fromKey || fundedDate > toKey) {
          return false;
        }
      }

      // LO filter
      if (filters.loanOfficer !== 'all') {
        if (getLoanOfficerName(loan) !== filters.loanOfficer) {
return false;
}
      }

      // Investor filter
      if (filters.investor !== 'all') {
        if (loan.investor_name !== filters.investor) {
return false;
}
      }

      // Channel filter
      if (filters.channel !== 'all') {
        if (loan.channel !== filters.channel) {
return false;
}
      }

      // Product type filter
      if (filters.productType !== 'all') {
        if (loan.rate_lock_loan_program !== filters.productType) {
return false;
}
      }

      return true;
    });
  }, [loans, filters, period]);

  // Compute summary metrics
  const summaryMetrics = useMemo(() => {
    const totalCount = filteredLoans.length;
    const totalVolume = filteredLoans.reduce(
      (sum, l) => sum + (l.trans_details_loan_amt || 0),
      0
    );
    const avgLoanSize = totalCount > 0 ? totalVolume / totalCount : 0;

    // Channel breakdown
    const byChannel = new Map<string, { count: number; volume: number }>();
    filteredLoans.forEach((loan) => {
      const ch = loan.channel || 'Unknown';
      const existing = byChannel.get(ch) || { count: 0, volume: 0 };
      existing.count += 1;
      existing.volume += loan.trans_details_loan_amt || 0;
      byChannel.set(ch, existing);
    });

    // Purchased count
    const purchasedCount = filteredLoans.filter((l) => l.purchased_date).length;

    // Average days in warehouse (funded to purchased, only for purchased loans)
    const daysValues: number[] = [];
    filteredLoans.forEach((loan) => {
      const fundedDate = getStrictFundedDate(loan);
      if (fundedDate && loan.purchased_date) {
        try {
          const days = differenceInDays(parseISO(loan.purchased_date), parseISO(fundedDate));
          if (days >= 0) {
daysValues.push(days);
}
        } catch {
          // skip invalid dates
        }
      }
    });
    const avgDaysInWarehouse =
      daysValues.length > 0
        ? daysValues.reduce((a, b) => a + b, 0) / daysValues.length
        : 0;

    return { totalCount, totalVolume, avgLoanSize, byChannel, purchasedCount, avgDaysInWarehouse };
  }, [filteredLoans]);

  // Apply non-date filters only (used by export Summary sheet for all 3 time periods)
  const applyNonDateFilters = (loanList: PipelineLoan[]): PipelineLoan[] => {
    return loanList.filter((loan) => {
      if (filters.loanOfficer !== 'all' && getLoanOfficerName(loan) !== filters.loanOfficer) {
return false;
}
      if (filters.investor !== 'all' && loan.investor_name !== filters.investor) {
return false;
}
      if (filters.channel !== 'all' && loan.channel !== filters.channel) {
return false;
}
      if (filters.productType !== 'all' && loan.rate_lock_loan_program !== filters.productType) {
return false;
}
      return true;
    });
  };

  // Filter loans by funded date within a period (ISO string comparison, inclusive)
  const filterByPeriod = (loanList: PipelineLoan[], from: Date, to: Date): PipelineLoan[] => {
    const fromStr = format(from, 'yyyy-MM-dd');
    const toStr = format(to, 'yyyy-MM-dd');
    return loanList.filter((loan) => {
      const fundedDate = getStrictFundedDate(loan);
      if (!fundedDate) {
return false;
}
      const dateOnly = fundedDate.slice(0, 10);
      return dateOnly >= fromStr && dateOnly <= toStr;
    });
  };

  const handleExport = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'LendWise';
      workbook.created = new Date();

      // --- Summary Sheet ---
      const summary = workbook.addWorksheet('Summary');
      const now = businessTodayAsLocalDate();
      const currentYear = now.getFullYear();
      const selectedPeriodLabel = `${period.label}: ${format(period.from, 'MM/dd/yyyy')} – ${format(period.to, 'MM/dd/yyyy')}`;

      const periods = [
        { label: `Selected Period — ${selectedPeriodLabel}`, from: period.from, to: period.to },
        { label: `Most Recent Fiscal Year Ended: 12/31/${currentYear - 1}`, from: new Date(currentYear - 1, 0, 1), to: new Date(currentYear - 1, 11, 31) },
        { label: `Previous Fiscal Year Ended: 12/31/${currentYear - 2}`, from: new Date(currentYear - 2, 0, 1), to: new Date(currentYear - 2, 11, 31) }
      ];

      // Apply non-date filters to all loans, then split by period
      const nonDateFiltered = applyNonDateFilters(loans);
      const periodLoans = [
        filteredLoans,
        ...periods.slice(1).map((p) => filterByPeriod(nonDateFiltered, p.from, p.to))
      ];
      const stateLabels = getStateLabels(filteredLoans, periodLoans.slice(1).flat());

      // Column widths: A=label(30), then 3 groups of 3 cols (18 each)
      // Center-align all data columns
      summary.getColumn(1).width = 30;
      for (let i = 2; i <= 10; i++) {
        summary.getColumn(i).width = 18;
        summary.getColumn(i).alignment = { horizontal: 'center' };
      }

      // Styling helpers
      const headerFill: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF333333' } };
      const headerFont: Partial<ExcelJS.Font> = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
      const boldFont: Partial<ExcelJS.Font> = { bold: true, size: 10 };
      const normalFont: Partial<ExcelJS.Font> = { size: 10 };
      const thinBorder: Partial<ExcelJS.Border> = { style: 'thin', color: { argb: 'FF000000' } };

      // Apply header fill+font to specific cells only (prevents bleed past last column)
      const applyHeaderStyle = (row: ExcelJS.Row, colCount: number) => {
        for (let c = 1; c <= colCount; c++) {
          const cell = row.getCell(c);
          cell.font = headerFont;
          cell.fill = headerFill;
        }
      };

      // Apply box borders to a rectangular cell range
      const applyBoxBorders = (startRow: number, endRow: number, startCol: number, endCol: number) => {
        for (let r = startRow; r <= endRow; r++) {
          for (let c = startCol; c <= endCol; c++) {
            const cell = summary.getCell(r, c);
            cell.border = {
              top: r === startRow ? thinBorder : { style: 'thin', color: { argb: 'FFD0D0D0' } },
              bottom: r === endRow ? thinBorder : { style: 'thin', color: { argb: 'FFD0D0D0' } },
              left: c === startCol || c === 2 || c === 5 || c === 8 ? thinBorder : { style: 'thin', color: { argb: 'FFD0D0D0' } },
              right: c === endCol || c === 4 || c === 7 || c === 10 ? thinBorder : { style: 'thin', color: { argb: 'FFD0D0D0' } }
            };
          }
        }
      };

      // Row 1: Company name
      const companyRow = summary.addRow(['Lendwise Mortgage Inc.']);
      companyRow.font = { bold: true, size: 14 };
      summary.mergeCells('A1:J1');

      // Row 2: Title
      const titleRow = summary.addRow([`Funded Production Summary — ${selectedPeriodLabel}`]);
      titleRow.font = { bold: true, size: 12 };
      summary.mergeCells('A2:J2');

      const scopeRow = summary.addRow([
        `Loan Officer: ${filters.loanOfficer === 'all' ? 'All' : filters.loanOfficer} | Investor: ${filters.investor === 'all' ? 'All' : filters.investor} | Channel: ${filters.channel === 'all' ? 'All' : filters.channel} | Product: ${filters.productType === 'all' ? 'All' : filters.productType}`
      ]);
      summary.mergeCells(scopeRow.number, 1, scopeRow.number, 10);
      scopeRow.font = normalFont;
      scopeRow.alignment = { wrapText: true, vertical: 'middle' };
      scopeRow.height = 30;
      summary.addRow([]);

      // Section definitions. `subUnder`/`subBuilder` optionally nest doc-type sub-rows beneath a
      // parent label (Non-QM) without affecting that section's TOTAL (which sums top-level rows).
      const sections: {
        title: string;
        labels: string[];
        categorize: (loan: PipelineLoan) => string;
        subUnder?: string;
        subBuilder?: (loans: PipelineLoan[]) => BreakdownRow[];
      }[] = [
        {
          title: 'Loan Type',
          labels: [...LOAN_TYPE_LABELS],
          categorize: getLoanTypeCategory,
          subUnder: 'Non-QM',
          subBuilder: buildNonQmSubBreakdown
        },
        { title: 'Purpose', labels: [...PURPOSE_LABELS], categorize: getPurposeCategory },
        { title: 'Volume by State', labels: stateLabels, categorize: getStateCategory },
        { title: 'Origination Channel', labels: [...CHANNEL_LABELS], categorize: getChannelCategory },
        { title: 'Fixed / ARM', labels: [...RATE_TYPE_LABELS], categorize: getRateTypeCategory }
      ];

      for (const section of sections) {
        // Period label row
        const periodLabelRow = summary.addRow([
          '',
          periods[0].label, '', '',
          periods[1].label, '', '',
          periods[2].label, '', ''
        ]);
        periodLabelRow.font = boldFont;
        // Merge period label cells
        const plRowNum = periodLabelRow.number;
        summary.mergeCells(plRowNum, 2, plRowNum, 4);
        summary.mergeCells(plRowNum, 5, plRowNum, 7);
        summary.mergeCells(plRowNum, 8, plRowNum, 10);
        // Center-align period labels
        for (const col of [2, 5, 8]) {
          periodLabelRow.getCell(col).alignment = { horizontal: 'center' };
        }

        // Sub-header row
        const subHeaderRow = summary.addRow([
          section.title,
          'Volume ($)', 'Volume (Units)', '% by Dollar Volume',
          'Volume ($)', 'Volume (Units)', '% by Dollar Volume',
          'Volume ($)', 'Volume (Units)', '% by Dollar Volume'
        ]);
        applyHeaderStyle(subHeaderRow, 10);
        if (section.subUnder) {
          for (const col of [4, 7, 10]) {
            subHeaderRow.getCell(col).note =
              'Indented Non-QM doc-type sub-rows show % of Non-QM volume (top-level rows show % of total).';
          }
        }

        // Build breakdowns for each period
        const breakdowns = periodLoans.map((pLoans) =>
          buildBreakdown(pLoans, section.categorize, section.labels)
        );

        // Data rows
        for (let i = 0; i < section.labels.length; i++) {
          const rowData: (string | number)[] = [section.labels[i]];
          for (let p = 0; p < 3; p++) {
            const row = breakdowns[p][i];
            rowData.push(row.volume, row.units, row.pct / 100);
          }
          const dataRow = summary.addRow(rowData);
          dataRow.font = normalFont;
          // Apply number formatting
          for (const col of [2, 5, 8]) {
dataRow.getCell(col).numFmt = '#,##0';
}
          for (const col of [4, 7, 10]) {
dataRow.getCell(col).numFmt = '0.0%';
}

          // Nested doc-type sub-rows under the configured parent label (Non-QM). Display-only:
          // not added to the section TOTAL. Percentages are share of Non-QM volume per period.
          if (section.subUnder && section.subBuilder && section.labels[i] === section.subUnder) {
            const subPerPeriod = periodLoans.map((pLoans) => section.subBuilder!(pLoans));
            const anyNonQm = subPerPeriod.some((subRows) => subRows.some((r) => r.units > 0));
            if (anyNonQm) {
              dataRow.getCell(1).note =
                'Indented rows break Non-QM out by income-doc type. % is share of Non-QM volume.';
              const subLabels = subPerPeriod[0].map((r) => r.label);
              for (let s = 0; s < subLabels.length; s++) {
                const subRowData: (string | number)[] = [`— ${subLabels[s]}`];
                for (let p = 0; p < 3; p++) {
                  const sr = subPerPeriod[p][s];
                  subRowData.push(sr.volume, sr.units, sr.pct / 100);
                }
                const subRow = summary.addRow(subRowData);
                subRow.font = { ...normalFont, italic: true, color: { argb: 'FF666666' } };
                subRow.getCell(1).alignment = { indent: 2 };
                for (const col of [2, 5, 8]) {
                  subRow.getCell(col).numFmt = '#,##0';
                }
                for (const col of [4, 7, 10]) {
                  subRow.getCell(col).numFmt = '0.0%';
                }
              }
            }
          }
        }

        // TOTAL row
        const totalData: (string | number)[] = ['TOTAL'];
        for (let p = 0; p < 3; p++) {
          const totalVolume = breakdowns[p].reduce((sum, r) => sum + r.volume, 0);
          const totalUnits = breakdowns[p].reduce((sum, r) => sum + r.units, 0);
          totalData.push(totalVolume, totalUnits, totalVolume > 0 ? 1 : 0);
        }
        const totalRow = summary.addRow(totalData);
        totalRow.font = boldFont;
        for (const col of [2, 5, 8]) {
totalRow.getCell(col).numFmt = '#,##0';
}
        for (const col of [4, 7, 10]) {
totalRow.getCell(col).numFmt = '0.0%';
}

        // Box borders around the entire section (period labels through TOTAL)
        applyBoxBorders(periodLabelRow.number, totalRow.number, 1, 10);

        // Blank separator
        summary.addRow([]);
      }

      // --- Details Sheet ---
      const details = workbook.addWorksheet('Details');
      const detailHeaders = [
        'Loan #', 'Borrower', 'Loan Officer', 'Amount', 'Rate',
        'Investor', 'Channel', 'Loan Type', 'Doc Type', 'Income Doc Detail', 'Product',
        'Funded Date', 'Purchased Date', 'Whse Days'
      ];
      const detailHeaderRow = details.addRow(detailHeaders);
      applyHeaderStyle(detailHeaderRow, detailHeaders.length);

      // Column widths
      const detailWidths = [14, 22, 18, 14, 10, 22, 12, 28, 20, 30, 30, 14, 14, 10];
      detailWidths.forEach((w, i) => {
 details.getColumn(i + 1).width = w; 
});

      // Details sheet exports the same filtered loans shown in the Details tab
      // (respects the selected date range + all active filters)
      const detailsLoans = filteredLoans;
      detailsLoans.forEach((loan) => {
        const fundedDate = getStrictFundedDate(loan);
        let whseDays: number | string = '';
        if (fundedDate && loan.purchased_date) {
          try {
            const d = differenceInDays(parseISO(loan.purchased_date), parseISO(fundedDate));
            if (d >= 0) {
whseDays = d;
}
          } catch {
            // skip
          }
        }

        const row = details.addRow([
          loan.trans_details_loan_number || '',
          getBorrowerName(loan),
          getLoanOfficerName(loan),
          loan.trans_details_loan_amt || 0,
          loan.trans_details_interest_rate || 0,
          (loan.investor_name || '').trim() || 'Other',
          loan.channel || '',
          getLoanTypeCategory(loan),
          getLoanTypeCategory(loan) === 'Non-QM' ? getNonQmSubtype(loan) : '',
          loan.cx_doc_type || '',
          loan.rate_lock_loan_program || '',
          fundedDate ? format(parseISO(fundedDate), 'MM/dd/yyyy') : '',
          loan.purchased_date ? format(parseISO(loan.purchased_date), 'MM/dd/yyyy') : '',
          whseDays
        ]);
        row.font = normalFont;
      });

      // Format detail columns
      details.getColumn(4).numFmt = '#,##0';
      details.getColumn(5).numFmt = '0.000"%"';

      // --- Monthly Production Sheet ---
      const monthly = workbook.addWorksheet('Monthly Production');

      const mpFrom = period.from;
      const mpTo = period.to;
      const sheetLoans = filteredLoans;

      // Column widths
      const mpColWidths = [16, 18, 10, 18, 18, 16];
      mpColWidths.forEach((w, i) => {
 monthly.getColumn(i + 1).width = w; 
});

      // Header rows
      const mpCompanyRow = monthly.addRow(['Lendwise Mortgage Inc.']);
      mpCompanyRow.font = { bold: true, size: 14 };
      monthly.mergeCells(`A1:F1`);

      const mpTitleRow = monthly.addRow(['Monthly Production Report']);
      mpTitleRow.font = { bold: true, size: 12 };
      monthly.mergeCells(`A2:F2`);

      const mpDateRow = monthly.addRow([`${format(mpFrom, 'MM/dd/yyyy')} – ${format(mpTo, 'MM/dd/yyyy')}`]);
      mpDateRow.font = { size: 10, italic: true };
      monthly.mergeCells(`A3:F3`);

      monthly.addRow([]); // blank separator

      // --- Section A: Month-over-Month Summary ---
      const mpSectionATitle = monthly.addRow(['Month-over-Month Production']);
      mpSectionATitle.getCell(1).font = { bold: true, size: 11 };

      const momHeaders = ['Month', 'Volume ($)', 'Units', 'Avg Loan Size', 'MoM Change ($)', 'MoM Change (%)'];
      const momHeaderRow = monthly.addRow(momHeaders);
      applyHeaderStyle(momHeaderRow, momHeaders.length);

      const monthBuckets = groupLoansByMonth(sheetLoans, mpFrom, mpTo);

      // Conditional fill colors
      const greenFill: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F5E9' } };
      const redFill: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFCE4EC' } };

      let totalVolume = 0;
      let totalUnits = 0;

      monthBuckets.forEach((bucket, idx) => {
        const volume = bucket.loans.reduce((s, l) => s + (l.trans_details_loan_amt || 0), 0);
        const units = bucket.loans.length;
        const avgSize = units > 0 ? volume / units : 0;

        totalVolume += volume;
        totalUnits += units;

        let momDelta: number | string = '';
        let momPct: number | string = '';

        if (idx > 0) {
          const prevBucket = monthBuckets[idx - 1];
          const prevVolume = prevBucket.loans.reduce((s, l) => s + (l.trans_details_loan_amt || 0), 0);
          momDelta = volume - prevVolume;
          momPct = prevVolume > 0 ? (volume - prevVolume) / prevVolume : '';
        }

        const row = monthly.addRow([bucket.label, volume, units, avgSize, momDelta, momPct]);
        row.font = normalFont;
        row.getCell(2).numFmt = '#,##0';
        row.getCell(4).numFmt = '$#,##0';
        if (typeof momDelta === 'number') {
          row.getCell(5).numFmt = '#,##0';
          if (momDelta > 0) {
            row.getCell(5).fill = greenFill;
            row.getCell(6).fill = greenFill;
          } else if (momDelta < 0) {
            row.getCell(5).fill = redFill;
            row.getCell(6).fill = redFill;
          }
        }
        if (typeof momPct === 'number') {
          row.getCell(6).numFmt = '0.0%';
        }
      });

      // Totals row
      const momTotalRow = monthly.addRow([
        'TOTAL', totalVolume, totalUnits,
        totalUnits > 0 ? totalVolume / totalUnits : 0,
        '', ''
      ]);
      momTotalRow.font = boldFont;
      momTotalRow.getCell(2).numFmt = '#,##0';
      momTotalRow.getCell(4).numFmt = '$#,##0';

      monthly.addRow([]); // blank separator

      // --- Section B: Top Investors ---
      const mpSectionBTitle = monthly.addRow(['Top Investors']);
      mpSectionBTitle.getCell(1).font = { bold: true, size: 11 };

      const invHeaders = ['Investor', 'Volume ($)', 'Units', '% of Total'];
      const invHeaderRow = monthly.addRow(invHeaders);
      applyHeaderStyle(invHeaderRow, invHeaders.length);

      const investorMap = new Map<string, { volume: number; units: number }>();
      sheetLoans.forEach((loan) => {
        const inv = (loan.investor_name || '').trim() || 'Other';
        const existing = investorMap.get(inv) || { volume: 0, units: 0 };
        existing.volume += loan.trans_details_loan_amt || 0;
        existing.units += 1;
        investorMap.set(inv, existing);
      });
      const investorRows = [...investorMap.entries()].sort((a, b) => b[1].volume - a[1].volume);

      investorRows.forEach(([name, data]) => {
        const row = monthly.addRow([
          name, data.volume, data.units,
          totalVolume > 0 ? data.volume / totalVolume : 0
        ]);
        row.font = normalFont;
        row.getCell(2).numFmt = '#,##0';
        row.getCell(4).numFmt = '0.0%';
      });

      monthly.addRow([]); // blank separator

      // --- Sections C/D/E: Breakdowns by Loan Type, Purpose, Channel ---
      const breakdownSections: {
        title: string;
        labels: string[];
        categorize: (loan: PipelineLoan) => string;
        subUnder?: string;
        subBuilder?: (loans: PipelineLoan[]) => BreakdownRow[];
      }[] = [
        {
          title: 'Volume by Loan Type',
          labels: [...LOAN_TYPE_LABELS],
          categorize: getLoanTypeCategory,
          subUnder: 'Non-QM',
          subBuilder: buildNonQmSubBreakdown
        },
        { title: 'Volume by Purpose', labels: [...PURPOSE_LABELS], categorize: getPurposeCategory },
        { title: 'Volume by Channel', labels: [...CHANNEL_LABELS], categorize: getChannelCategory }
      ];

      for (const section of breakdownSections) {
        const sectionTitle = monthly.addRow([section.title]);
        sectionTitle.getCell(1).font = { bold: true, size: 11 };

        const bHeaders = [section.title.replace('Volume by ', ''), 'Volume ($)', 'Units', '% of Total'];
        const bHeaderRow = monthly.addRow(bHeaders);
        applyHeaderStyle(bHeaderRow, bHeaders.length);
        if (section.subUnder) {
          bHeaderRow.getCell(4).note =
            'Indented Non-QM doc-type sub-rows show % of Non-QM volume (top-level rows show % of total).';
        }

        const breakdown = buildBreakdown(sheetLoans, section.categorize, section.labels);
        const subRows = section.subBuilder ? section.subBuilder(sheetLoans) : [];
        const showSub = subRows.some((r) => r.units > 0);
        breakdown.forEach((br) => {
          const row = monthly.addRow([br.label, br.volume, br.units, br.pct / 100]);
          row.font = normalFont;
          row.getCell(2).numFmt = '#,##0';
          row.getCell(4).numFmt = '0.0%';

          // Nested Non-QM doc-type sub-rows (display-only; excluded from the section TOTAL below).
          if (section.subUnder && br.label === section.subUnder && showSub) {
            row.getCell(1).note =
              'Indented rows break Non-QM out by income-doc type. % is share of Non-QM volume.';
            subRows.forEach((sr) => {
              const subRow = monthly.addRow([`— ${sr.label}`, sr.volume, sr.units, sr.pct / 100]);
              subRow.font = { ...normalFont, italic: true, color: { argb: 'FF666666' } };
              subRow.getCell(1).alignment = { indent: 2 };
              subRow.getCell(2).numFmt = '#,##0';
              subRow.getCell(4).numFmt = '0.0%';
            });
          }
        });

        // Section total
        const bTotalVol = breakdown.reduce((s, r) => s + r.volume, 0);
        const bTotalUnits = breakdown.reduce((s, r) => s + r.units, 0);
        const bTotalRow = monthly.addRow(['TOTAL', bTotalVol, bTotalUnits, bTotalVol > 0 ? 1 : 0]);
        bTotalRow.font = boldFont;
        bTotalRow.getCell(2).numFmt = '#,##0';
        bTotalRow.getCell(4).numFmt = '0.0%';

        monthly.addRow([]); // blank separator
      }

      // Download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `FundedLoans_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      log.info('Exported funded loan report', {
        metadata: {
          detailCount: detailsLoans.length,
          periods: periods.map((p, i) => ({ label: p.label, loans: periodLoans[i].length }))
        }
      });
    } catch (error) {
      log.error('Failed to export funded loan report', {}, error as Error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <DollarSign className="h-6 w-6 text-primary" />
            Funded Loan Report
          </h1>
          <p className="text-muted-foreground">
            Historical funded production by funded date
          </p>
          {refreshedAt && (
            <p className="mt-1 text-xs text-muted-foreground">
              Refreshed {format(refreshedAt, 'MMM d, yyyy h:mm a')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={isLoading || loans.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={fetchLoans}
            disabled={isLoading}
            aria-label="Refresh funded loan report"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {loadError && (
        <div role="alert" className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {loadError}
        </div>
      )}
      {dataWarnings.map((warning) => (
        <div key={warning} role="status" className="flex items-center gap-2 rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-sm">
          <AlertTriangle className="h-4 w-4 shrink-0 text-warning" />
          {warning}
        </div>
      ))}

      {/* Filters */}
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex min-w-0 items-center gap-4">
          {/* Date Range Picker */}
          <ReportDateRangePicker
            className="max-w-full flex-wrap justify-start"
            value={period}
            onChange={setPeriod}
            max={businessTodayAsLocalDate()}
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* LO Filter */}
          <Select
            value={filters.loanOfficer}
            onValueChange={(value) =>
              setFilters((prev) => ({ ...prev, loanOfficer: value }))
            }
          >
            <SelectTrigger aria-label="Loan officer" className="w-[180px]">
              <SelectValue placeholder="All Loan Officers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Loan Officers</SelectItem>
              {loanOfficers.map((lo) => (
                <SelectItem key={lo} value={lo}>
                  {lo}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Investor Filter */}
          <Select
            value={filters.investor}
            onValueChange={(value) =>
              setFilters((prev) => ({ ...prev, investor: value }))
            }
          >
            <SelectTrigger aria-label="Investor" className="w-[200px]">
              <SelectValue placeholder="All Investors" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Investors</SelectItem>
              {investors.map((investor) => (
                <SelectItem key={investor} value={investor}>
                  {investor}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Channel Filter */}
          <Select
            value={filters.channel}
            onValueChange={(value) =>
              setFilters((prev) => ({ ...prev, channel: value }))
            }
          >
            <SelectTrigger aria-label="Channel" className="w-[160px]">
              <SelectValue placeholder="All Channels" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Channels</SelectItem>
              {channels.map((channel) => (
                <SelectItem key={channel} value={channel}>
                  {channel}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Product Type Filter */}
          <Select
            value={filters.productType}
            onValueChange={(value) =>
              setFilters((prev) => ({ ...prev, productType: value }))
            }
          >
            <SelectTrigger aria-label="Product" className="w-[200px]">
              <SelectValue placeholder="All Products" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Products</SelectItem>
              {productTypes.map((product) => (
                <SelectItem key={product} value={product}>
                  {product}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Tabs: Summary / Details / Monthly Production */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'summary' | 'details' | 'monthly-production')}>
        <TabsList className="h-auto max-w-full flex-wrap">
          <TabsTrigger value="summary" className="gap-1.5">
            <BarChart3 className="h-4 w-4" />
            Summary
          </TabsTrigger>
          <TabsTrigger value="details" className="gap-1.5">
            <Table2 className="h-4 w-4" />
            Details
          </TabsTrigger>
          <TabsTrigger value="monthly-production" className="gap-1.5">
            <CalendarRange className="h-4 w-4" />
            Monthly Production
          </TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="mt-4">
          <FundedSummaryReport loans={filteredLoans} isLoading={isLoading} />
        </TabsContent>

        <TabsContent value="details" className="mt-4 space-y-6">
          {/* Summary Cards */}
          <FundedSummaryCards
            totalCount={summaryMetrics.totalCount}
            totalVolume={summaryMetrics.totalVolume}
            avgLoanSize={summaryMetrics.avgLoanSize}
            avgDaysInWarehouse={summaryMetrics.avgDaysInWarehouse}
            byChannel={summaryMetrics.byChannel}
            purchasedCount={summaryMetrics.purchasedCount}
          />

          {/* Data Table */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">Funded Loan Details</CardTitle>
            </CardHeader>
            <CardContent>
              <FundedDataTable
                loans={filteredLoans}
                isLoading={isLoading}
                getStrictFundedDate={getStrictFundedDate}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="monthly-production" className="mt-4">
          <MonthlyProductionReport
            loans={filteredLoans}
            dateRange={{ from: period.from, to: period.to }}
            isLoading={isLoading}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default FundedLoanReport;
